import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ArticleDescription, EmployeeInfo, ProductionOrderManualModeFinishingDataViewModel, Quantity } from 'chronos-core-client';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { EmployeeRegistrationDsService } from '@app/core/data-services';
import { TranslateService } from '@ngx-translate/core';
import { ColumnType, StatusPanel } from '../../models';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-multi-user-worktime-table',
  templateUrl: './multi-user-worktime-table.component.html',
  styleUrls: ['./multi-user-worktime-table.component.scss']
})
export class MultiUserWorktimeTableComponent implements OnInit, OnDestroy {
  @Input() public manualModeFinishData: ProductionOrderManualModeFinishingDataViewModel;
  @Input() public form: UntypedFormGroup;
  @Input() public article?: ArticleDescription;
  @Output() public quantityPanelChanged = new EventEmitter<StatusPanel>();
  @Output() public formStatusChanged = new EventEmitter<boolean>();

  public minValue = 0;
  public maxMinutes = 59;

  public employeesSuggestionList: EmployeeInfo[];
  public employees: EmployeeInfo[];
  public formGroupGrossQuantity: Quantity = { value: 0, unitId: '' };
  public orderGrossQuantity: Quantity;
  public duration = '00 h 00 min';
  public qtyStatusPanel: StatusPanel;
  public readonly COLUMN_TYPE = ColumnType;

  private subscriptions = new Subscription();
  private destroy$ = new Subject<void>();

  public readonly TEXT_MINUTES = 'minutes';
  public readonly TEXT_HOURS = 'hours';
  public readonly INPUT_STYLE = { width: '5em', textAlign: 'end' };
  public readonly INPUT_STYLE_QTY = { width: '8em', textAlign: 'end' };
  public readonly INPUT_STYLE_AUTOCOMPLETE = { width: '15em' };
  public readonly QUANTITY_MAX_LIMIT = 99999999;
  public readonly HOURS_MAX_LIMIT = 24;

  public readonly MINUTES_MAX_LIMIT = 59;
  
  constructor(
    private formBuilder: UntypedFormBuilder,
    private employeeRegistrationDsService: EmployeeRegistrationDsService,
    private translate: TranslateService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.add(
      this.employeeRegistrationDsService
        .getEmployees()
        .pipe(takeUntil(this.destroy$))
        .subscribe((employees) => {
          this.employees = employees;
          this.initModalForm(this.manualModeFinishData);
        })
    );

    this.subscriptions.add(
      this.rows.valueChanges
        .pipe(debounceTime(150), takeUntil(this.destroy$))
        .subscribe(() => {
          this.calculateFooterValues();
        })
    );
  }

  public get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

  public removeRow(index: number): void {
    if (this.rows.length > 1) {
      this.rows.removeAt(index);
    }

    this.calculateFooterValues();
  }

  public filterAutoCompleteEmployees(event): void {
    const empList = this.form.value.rows.map((element) => ({
      employee: typeof element.employee === 'object' && element.employee !== null ? element.employee.name : element.employee
    }));

    this.employeesSuggestionList = this.employees.filter((employee) => employee.name.toLowerCase().includes(event.query.toLowerCase()));
    this.employeesSuggestionList = this.employeesSuggestionList.filter((empA) => !empList?.some((empB) => empA.name === empB.employee));
  }

  public isRowValid(index: number): boolean {
    const userRow = this.form.value.rows[index];
    return !(!!userRow.employee && (
        (userRow.hours > 0 || userRow.minutes > 0) && 
        !(userRow.hours > this.HOURS_MAX_LIMIT || userRow.minutes > this.MINUTES_MAX_LIMIT) &&
        !(userRow.hours >= this.HOURS_MAX_LIMIT && userRow.minutes > 0)) && userRow.quantity > 0);
  }

  public addFormRow(): void {
    const row = this.createRow();
    this.rows.push(row);

    this.calculateFooterValues();
  }

  public checkMaxLimit(index: number) {
    const row = this.rows.controls[index];
    const hours = row.get('hours') as FormControl;
    const minutes = row.get('minutes') as FormControl;

    if (hours.value > this.HOURS_MAX_LIMIT) {
      hours.setValue(this.HOURS_MAX_LIMIT, { emitEvent: false });
      hours.markAsDirty();
    }

    if (minutes.value > this.MINUTES_MAX_LIMIT) {
      minutes.setValue(this.MINUTES_MAX_LIMIT, { emitEvent: false });
      minutes.markAsDirty();
    }

    if (hours.value === this.HOURS_MAX_LIMIT && minutes.value > 0) {
      minutes.setValue(0, { emitEvent: false });
      minutes.markAsDirty();
    }

    this.calculateFooterValues();
  }

  private initModalForm(orderFinishData: ProductionOrderManualModeFinishingDataViewModel): void {
    this.formGroupGrossQuantity = { value: 0, unitId: orderFinishData.orderGrossQuantity.unitId };
    this.orderGrossQuantity = orderFinishData.orderGrossQuantity;

    if (orderFinishData.runEmployeeWorkTimeData.length === 0) {
      this.addFormRow();
      this.statusPanel(0);
      return;
    }

    // Clear all existing rows once before loop
    this.rows.clear();

    let hours: any;
    let minutes: any;

    orderFinishData.runEmployeeWorkTimeData.forEach((r) => {
      const row = this.createRow();
      this.rows.push(row);

      if (r?.duration) {
        const obj = this.parseDuration(r?.duration);
        hours = obj.days * 24 + obj.hours;
        minutes = obj.minute;
      } else {
        hours = 0;
        minutes = 0;
      }

      row.patchValue(
        {
          employee: this.employees.find((x) => x.id === r.employeeId),
          hours,
          minutes,
          quantity: r.quantity.value
        },
        { emitEvent: false }
      );
    });

    this.calculateFooterValues();
  }

  private calculateFooterValues(): void {
    let workerCount = 0;
    let totalMinutes = 0;
    let totalQuantity = 0;
    
    this.rows.controls.forEach((row: AbstractControl) => {
      if (row.invalid) {
        return;
      }
    
      workerCount++;
    
      const hours = Number(row.get('hours')?.value) || 0;
      const minutes = Number(row.get('minutes')?.value) || 0;
      const quantity = Number(row.get('quantity')?.value) || 0;
    
      totalMinutes += hours * 60 + minutes;
      totalQuantity += quantity;
    });
  
    const displayHours = Math.floor(totalMinutes / 60);
    const displayMinutes = totalMinutes % 60;
  
    this.duration = `${displayHours} ${this.translate.instant('FINISH_ORDER_MODAL.HOUR')}
      ${displayMinutes.toString().padStart(2, '0')} ${this.translate.instant('FINISH_ORDER_MODAL.MINUTE')}`;
  
    this.formGroupGrossQuantity = {
      value: totalQuantity,
      unitId: this.manualModeFinishData.orderGrossQuantity.unitId
    };
  
    this.statusPanel(displayHours);
 
    this.formStatusChanged.emit(
      this.form.valid &&
      totalQuantity === this.orderGrossQuantity?.value
    );
  }

  private statusPanel(hours: number) {
    const qty = this.orderGrossQuantity.value - this.formGroupGrossQuantity.value;
    this.qtyStatusPanel = {
      quantity: { value: qty, unitId: this.orderGrossQuantity.unitId },
      status: qty !== 0 ? (qty < 0 ? 'negative' : null) : 'success',
      totalHours: hours
    };

    this.quantityPanelChanged.emit(this.qtyStatusPanel);
  }

  private parseDuration(duration: string): any {
    const [daysAndHours, minutes, seconds] = duration.split(':');

    let days: number = 0;
    let hours: number;

    if (daysAndHours.includes('.')) {
      const [daysPart, hoursPart] = daysAndHours.split('.');
      days = parseInt(daysPart, 10);
      hours = parseInt(hoursPart, 10);
    } else {
      hours = parseInt(daysAndHours, 10);
    }

    const minute = parseInt(minutes, 10);

    return { days, hours, minute };
  }

  private createRow(): FormGroup {
    return this.formBuilder.group(
      {
        employee: [null, [Validators.required]],
        hours: [0, [Validators.required, Validators.min(0)]],
        minutes: [0, [Validators.required, Validators.min(0)]],
        quantity: [0, [Validators.required, Validators.min(1)]]
      },
      { validators: [validateTime] }
    );
  }  
}

function validateTime(formGroup: UntypedFormGroup): ValidationErrors | null {
  return formGroup.value.minutes === formGroup.value.hours && formGroup.value.hours === 0 ? { invalidTime: true } : null;
}
